import { ChevronLeft } from '@/components/icons/chevron-left';
import { ChevronRight } from '@/components/icons/chevron-right';
import {
  Swiper,
  SwiperSlide,
  SwiperOptions,
  Navigation,
  Thumbs,
  FreeMode,
} from '@/components/ui/slider';
import { Image } from '@/components/ui/image';
import { ProductImageZoom } from '@/components/ui/product-image-zoom';
import { useRef, useState } from 'react';
import { productPlaceholder } from '@/lib/placeholders';
import { resolveProductImageSrc } from '@/lib/optimizable-image-url';
import { rewriteStaticAssetUrl } from '@/lib/static-asset-url';
import { useIsRTL } from '@/lib/locals';
import cn from 'classnames';
import { PlayIcon } from '../icons/play-icon';
import { BulaThumb } from '@/components/products/details/bula-thumb';

interface Props {
  gallery: any[];
  video?: any;
  hideThumbs?: boolean;
  aspectRatio?: 'auto' | 'square';
  isModal?: boolean;
  bula?: {
    pdfUrl: string;
  } | null;
  /** Nome do produto para alt das imagens (acessibilidade e SEO) */
  productName?: string;
}
// product gallery breakpoints
const galleryCarouselBreakpoints = {
  320: {
    slidesPerView: 2,
  },
  480: {
    slidesPerView: 3,
  },
  640: {
    slidesPerView: 3,
  },
  800: {
    slidesPerView: 4,
  },
};
const swiperParams: SwiperOptions = {
  slidesPerView: 1,
  spaceBetween: 0,
};
export const ThumbsCarousel: React.FC<Props> = ({
  gallery,
  video,
  hideThumbs = false,
  aspectRatio = 'square',
  isModal = false,
  bula,
  productName,
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const { isRTL } = useIsRTL();
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const galleryLabel = productName
    ? `Galeria de imagens: ${productName}`
    : 'Galeria de imagens do produto';
  return (
    <div role="region" aria-label={galleryLabel} style={{ contain: 'layout style' }}>
      <div className="relative">
        <Swiper
          id="productGallery"
          modules={[Navigation, Thumbs, FreeMode]}
          thumbs={{
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          navigation={{
            prevEl: prevRef.current!, // Assert non-null
            nextEl: nextRef.current!, // Assert non-null
          }}
          {...swiperParams}
        >
          {gallery?.map((item: any, index: number) => (
            <SwiperSlide
              key={`product-gallery-${item.id}`}
              className="!flex items-center justify-center selection:bg-transparent"
            >
              <div
                className={cn(
                  'w-full h-full min-h-[280px] md:min-h-[380px] flex items-center justify-center',
                  !isModal && 'rounded-xl mb-4'
                )}
              >
                <ProductImageZoom
                  src={resolveProductImageSrc(item?.original, productPlaceholder)}
                  alt={productName ? `${productName} - Imagem ${index + 1}` : `Imagem do produto ${index + 1}`}
                  width={aspectRatio === 'square' ? 450 : 420}
                  height={aspectRatio === 'square' ? 450 : 560}
                  className={!isModal ? 'rounded-xl' : undefined}
                  priority={index === 0}
                />
              </div>
            </SwiperSlide>
          ))}
          {video?.length
            ? video.map((item: any, index: number) => (
                <SwiperSlide key={`product-video-${index}`}>
                  {item.url.includes('iframe') ? (
                    <div
                      className="product-video-iframe"
                      dangerouslySetInnerHTML={{
                        __html: rewriteStaticAssetUrl(item.url),
                      }}
                    />
                  ) : (
                    <div className="product-video-iframe">
                      <video controls src={rewriteStaticAssetUrl(item.url)}>
                        <track kind="captions" />
                      </video>
                    </div>
                  )}
                </SwiperSlide>
              ))
            : null}
        </Swiper>
        <button
          type="button"
          ref={prevRef}
          aria-label={isRTL ? 'Imagem seguinte' : 'Imagem anterior'}
          className="absolute z-10 flex items-center justify-center w-8 h-8 -mt-4 transition-all duration-200 border rounded-full shadow-xl cursor-pointer product-gallery-prev top-2/4 border-border-200 border-opacity-70 bg-light text-heading hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ltr:-left-4 rtl:-right-4 md:-mt-5 md:h-9 md:w-9 ltr:md:-left-5 rtl:md:-right-5"
        >
          {isRTL ? (
            <ChevronRight className="w-4 h-4" aria-hidden />
          ) : (
            <ChevronLeft className="w-4 h-4" aria-hidden />
          )}
        </button>
        <button
          type="button"
          ref={nextRef}
          aria-label={isRTL ? 'Imagem anterior' : 'Imagem seguinte'}
          className="absolute z-10 flex items-center justify-center w-8 h-8 -mt-4 transition-all duration-200 border rounded-full shadow-xl cursor-pointer product-gallery-next top-2/4 border-border-200 border-opacity-70 bg-light text-heading hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ltr:-right-4 rtl:-left-4 md:-mt-5 md:h-9 md:w-9 ltr:md:-right-5 rtl:md:-left-5"
        >
          {isRTL ? (
            <ChevronLeft className="w-4 h-4" aria-hidden />
          ) : (
            <ChevronRight className="w-4 h-4" aria-hidden />
          )}
        </button>
      </div>
      {/* End of product main slider */}
      <div
        className={cn('relative mx-auto max-w-md lg:pb-4', {
          hidden: hideThumbs,
        })}
      >
        <Swiper
          id="productGalleryThumbs"
          onSwiper={setThumbsSwiper}
          spaceBetween={20}
          watchSlidesProgress={true}
          freeMode={true}
          modules={[Navigation, Thumbs, FreeMode]}
          observer={true}
          observeParents={true}
          preventClicks={false}
          preventClicksPropagation={false}
          breakpoints={galleryCarouselBreakpoints}
        >
          {bula && (
            <SwiperSlide
              key="product-thumb-bula"
              className="!flex items-center justify-center"
            >
              <BulaThumb pdfUrl={bula.pdfUrl} className="w-20 h-20" />
            </SwiperSlide>
          )}
          {gallery?.map((item: any, index: number) => (
            <SwiperSlide
              key={`product-thumb-gallery-${item.id}`}
              className="!flex cursor-pointer items-center justify-center overflow-hidden rounded border border-border-200 border-opacity-75 hover:opacity-75"
            >
              <div className="relative w-20 h-20">
                <Image
                  unoptimized={!(item?.thumbnail && String(item.thumbnail).startsWith('http'))}
                  src={item?.thumbnail ?? productPlaceholder}
                  alt={productName ? `${productName} - Miniatura ${index + 1}` : `Miniatura do produto ${index + 1}`}
                  fill
                  sizes="80px"
                  className="object-contain"
                />
              </div>
            </SwiperSlide>
          ))}
          {video?.length
            ? video.map((item: any, index: number) => (
                <SwiperSlide
                  key={`product-video-${index}`}
                  className="relative flex items-center justify-center overflow-hidden border border-opacity-75 rounded cursor-pointer border-border-200 hover:opacity-75"
                >
                  <span className="sr-only">
                    {productName
                      ? `Miniatura do vídeo: ${productName}`
                      : 'Miniatura do vídeo do produto'}
                  </span>
                  <div className="w-20 h-20" aria-hidden />
                  <div
                    className="absolute flex items-center justify-center w-12 h-12 text-white -translate-x-1/2 -translate-y-1/2 rounded-full top-1/2 left-1/2 bg-accent-400"
                    aria-hidden
                  >
                    <PlayIcon className="w-4 h-4" />
                  </div>
                </SwiperSlide>
              ))
            : null}
        </Swiper>
      </div>
    </div>
  );
};
